import retry from 'p-retry';

import { mergeGuestPayloads, shouldAllowGuestRemoval } from './guestMerge';
import { GuestApiPayload, guestToApiPayload, parseGuestFromApi } from './guestMapper';
import { guestSyncChannel } from './guestSyncChannel';
import { useGuestDataStore } from '../../stores/guestDataStore/guestDataStore';
import { useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';
import { UserService } from '../user/userService';
import { AdditionalQuestion } from '../../types/guest';

export interface GuestsDataResponse {
  data: GuestApiPayload[];
  version: number;
  updatedAt: string;
}

export interface GuestBackupMetadata {
  id: string;
  createdAt: string;
  guestCount: number;
}

export interface UpdateGuestsDataPayload {
  localGuests: GuestApiPayload[];
  abortSignal?: AbortSignal;
  removedGuestIds?: string[];
  mergeBeforeSave?: boolean;
  allowGuestRemoval?: boolean;
  forceBulkRemoval?: boolean;
}

export interface SaveGuestsResult {
  version: number;
  updatedAt: string;
}

export class GuestService {
  private static apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'api';

  public static async getGuestsData(payload?: { abortSignal?: AbortSignal }): Promise<GuestsDataResponse> {
    const abortController = new AbortController();
    const abortSignal = payload?.abortSignal;

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => abortController.abort(abortSignal.reason), { once: true });
    }

    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/guests`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
          signal: abortController.signal,
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message, abortController);
        }

        const body = await response.json();
        const parsedData = (body.data ?? []).map((guest: Record<string, unknown>) => parseGuestFromApi(guest));
        const metadata = {
          version: Number(body.version ?? 0),
          updatedAt: String(body.updatedAt ?? new Date(0).toISOString()),
        };

        useGuestDataStore().setMetadata(metadata);

        return {
          data: parsedData,
          ...metadata,
        };
      },
      {
        retries: 2,
        signal: abortController.signal,
      },
    );

    return result;
  }

  public static async updateGuestsData(payload: UpdateGuestsDataPayload): Promise<SaveGuestsResult> {
    const {
      localGuests,
      abortSignal,
      removedGuestIds,
      mergeBeforeSave = true,
      allowGuestRemoval,
      forceBulkRemoval,
    } = payload;

    const performSave = async (): Promise<SaveGuestsResult> => {
      let dataToSave = localGuests;
      let expectedVersion = useGuestDataStore().getVersion;
      let serverGuestCount = dataToSave.length;

      if (mergeBeforeSave) {
        const serverState = await this.getGuestsData({
          ...(abortSignal !== undefined ? { abortSignal } : {}),
        });
        serverGuestCount = serverState.data.length;
        dataToSave = mergeGuestPayloads(
          serverState.data,
          localGuests,
          removedGuestIds !== undefined ? { removedGuestIds } : {},
        );
        expectedVersion = serverState.version;
      }

      const resolvedAllowGuestRemoval =
        allowGuestRemoval ?? shouldAllowGuestRemoval(serverGuestCount, dataToSave.length, removedGuestIds);

      const userStore = useUserTokensStore();

      const response = await fetch(`${this.apiBaseUrl}/guests`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
        },
        body: JSON.stringify({
          data: dataToSave,
          expectedVersion,
          allowGuestRemoval: resolvedAllowGuestRemoval,
          ...(forceBulkRemoval !== undefined ? { forceBulkRemoval } : {}),
        }),
        ...(abortSignal !== undefined ? { signal: abortSignal } : {}),
      });

      const success = response.status >= 200 && response.status <= 299;

      if (!success) {
        const responseBody = await response.json();
        const message = responseBody.message ?? responseBody.error?.message ?? 'Save failed';

        if (response.status === 409) {
          throw new GuestVersionConflictError(message);
        }

        await this.handleError(response.status, message);
      }

      const body = await response.json();
      const result = {
        version: Number(body.version ?? expectedVersion + 1),
        updatedAt: String(body.updatedAt ?? new Date().toISOString()),
      };

      useGuestDataStore().setMetadata(result);
      guestSyncChannel.notifyUpdated(result.version, result.updatedAt);

      return result;
    };

    return retry(performSave, {
      retries: 3,
      onFailedAttempt: (error) => {
        if (error instanceof GuestVersionConflictError && error.attemptNumber === 1) {
          return;
        }

        console.error(`Attempt ${error.attemptNumber} failed. There are ${error.retriesLeft} retries left.`);
      },
    });
  }

  public static async saveGuestChanges(payload: {
    localGuests: GuestApiPayload[];
    abortSignal?: AbortSignal;
    removedGuestIds?: string[];
  }): Promise<SaveGuestsResult> {
    return this.updateGuestsData({
      localGuests: payload.localGuests,
      mergeBeforeSave: true,
      ...(payload.abortSignal !== undefined ? { abortSignal: payload.abortSignal } : {}),
      ...(payload.removedGuestIds !== undefined ? { removedGuestIds: payload.removedGuestIds } : {}),
    });
  }

  public static async getGuestBackups(): Promise<GuestBackupMetadata[]> {
    const userStore = useUserTokensStore();

    const response = await fetch(`${this.apiBaseUrl}/guests/backups`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
      },
    });

    if (!response.ok) {
      const responseBody = await response.json();
      const message = responseBody.message ?? 'Failed to load backups';
      await this.handleError(response.status, message);
    }

    const body = await response.json();
    return body.backups ?? [];
  }

  public static async restoreGuestBackup(backupId: string): Promise<SaveGuestsResult & { guestCount: number }> {
    const userStore = useUserTokensStore();

    const response = await fetch(`${this.apiBaseUrl}/guests/backups/${encodeURIComponent(backupId)}/restore`, {
      method: 'POST',
      body: JSON.stringify({}),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
      },
    });

    if (!response.ok) {
      const responseBody = await response.json();
      const message = responseBody.message ?? 'Failed to restore backup';
      await this.handleError(response.status, message);
    }

    const body = await response.json();
    const result = {
      version: Number(body.version),
      updatedAt: String(body.updatedAt),
      guestCount: Number(body.guestCount),
    };

    useGuestDataStore().setMetadata(result);
    guestSyncChannel.notifyUpdated(result.version, result.updatedAt);

    return result;
  }

  public static guestToApiPayload = guestToApiPayload;

  public static async sendShareLinks(guestEmails: Array<string>): Promise<void> {
    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/users/guests/links/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
            Accept: 'application/json',
          },
          body: JSON.stringify({
            emails: guestEmails,
          }),
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }
      },
      {
        retries: 2,
      },
    );

    return result;
  }

  public static async createShareLink(): Promise<string> {
    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/users/guests/links`, {
          method: 'POST',
          body: JSON.stringify({}),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
            Accept: 'application/json',
          },
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }

        return response.json();
      },
      {
        retries: 2,
      },
    );

    return result.link;
  }

  private static async handleError(status: number, message: string, abortController?: AbortController): Promise<void> {
    if (status === 401) {
      const userStore = useUserTokensStore();

      if (userStore.getLoggedInUserRefreshToken) {
        const response = await UserService.refreshToken({ refreshToken: userStore.getLoggedInUserRefreshToken });

        const { accessToken, expiresIn, refreshToken: newRefreshToken } = response;

        userStore.setUserTokens({
          accessToken,
          refreshToken: newRefreshToken,
          expiresIn,
        });

        throw new Error('Token expired. Retrying...');
      }

      throw new Error('Session expired. Login again.');
    } else if (status === 404 && abortController) {
      abortController.abort('Resource not found.');
    } else {
      throw new Error(message);
    }
  }
}

export class GuestVersionConflictError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'GuestVersionConflictError';
  }
}

export type { AdditionalQuestion };
